import React, { FC, useCallback, useEffect } from 'react';

import { ELoginMethod } from '@webtrader/core/api/user/types';
import { EDataTestId } from '@webtrader/core/constants/test';
import { getDialCodeFromCountryKey } from '@webtrader/core/format/country';
import { ILoginCredentialsFormValue, mapLoginSubTabToOTPType } from '@webtrader/core/format/login';
import { useTranslation } from '@webtrader/core/i18n';
import { useAuthWithKeys } from '@webtrader/core/services/auth';
import { countryService, useCountry } from '@webtrader/core/services/country';

import { StyledLoginForm, StyledLoginFormActions, StyledLoginFormButton } from '../styled';
import LoginOtpFormEmailInput from './email';
import { useLoginOtpFormHook } from './hooks';
import LoginOtpFormMobileInput from './mobile';
import LoginOtpFormVerificationField from './verification';

interface ILoginOtpFormProps {
  loginMethod: ELoginMethod;
  values: ILoginCredentialsFormValue;
  setFieldValue: <FieldName extends keyof ILoginCredentialsFormValue>(
    fieldName: FieldName,
    value: ILoginCredentialsFormValue[FieldName]
  ) => void;
  submitForm: () => void;
}

const LoginOtpForm: FC<ILoginOtpFormProps> = (props) => {
  const { loginMethod, values, setFieldValue, submitForm } = props;
  const { email, phoneNumber, countryKey, verificationCode } = values || {};

  const { t } = useTranslation();
  const { loading, requestOtpLoading } = useAuthWithKeys(['loading', 'requestOtpLoading']);
  const { countryDetails = [] } = useCountry();

  const {
    isEmailError,
    isPhoneNumberError,
    isVerificationCodeError,
    isDisabledVerification,
    isDisabledSubmit
  } = useLoginOtpFormHook({ loginMethod, values });

  const handleEmailChange = useCallback(
    (value?: string) => {
      setFieldValue('email', value);
    },
    [setFieldValue]
  );

  const handlePhoneNumberChange = useCallback(
    (value?: string) => {
      setFieldValue('phoneNumber', value);
    },
    [setFieldValue]
  );

  const handleDialCodeChange = useCallback(
    (value?: string) => {
      setFieldValue('countryKey', value);
    },
    [setFieldValue]
  );

  const handleVerificationCodeChange = useCallback(
    (value?: string) => {
      setFieldValue('verificationCode', value);
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!countryDetails.length) {
      countryService.init();
    }
  }, [countryDetails]);

  return (
    <StyledLoginForm>
      {loginMethod === ELoginMethod.EMAIL ? (
        <LoginOtpFormEmailInput value={email} isError={isEmailError} onEmailChange={handleEmailChange} />
      ) : (
        <LoginOtpFormMobileInput
          value={phoneNumber}
          countryKey={countryKey}
          isError={isPhoneNumberError}
          onPhoneNumberChange={handlePhoneNumberChange}
          onDialCodeChange={handleDialCodeChange}
        />
      )}
      <LoginOtpFormVerificationField
        dialCode={getDialCodeFromCountryKey(countryKey)}
        email={email}
        isDisabled={isDisabledVerification}
        isError={isVerificationCodeError}
        isOtpLoading={requestOtpLoading}
        key={loginMethod}
        otpType={mapLoginSubTabToOTPType(loginMethod)}
        phoneNumber={phoneNumber}
        value={verificationCode}
        onVerificationCodeChange={handleVerificationCodeChange}
      />

      <StyledLoginFormActions>
        <StyledLoginFormButton
          disabled={isDisabledSubmit}
          loading={!isDisabledSubmit && loading}
          onPress={submitForm}
          testID={EDataTestId.LOGIN_SUBMIT}
        >
          {t('SIGN IN')}
        </StyledLoginFormButton>
      </StyledLoginFormActions>
    </StyledLoginForm>
  );
};

export default LoginOtpForm;
